import printingSettingsSlice from '@event/store/printingSettingsSlice';
import { wordpressApi } from '@blog/services';
import { Middleware, MiddlewareAPI, configureStore, isRejected } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { api } from '@services/api';
import { djangoCmsApi } from '@services/djangoCmsApi';
import localeApi from '@services/locale';
import translationApi from '@services/translation';
import appSlice from '@store/appSlice';
import breadcrumbSlice from '@store/breadcrumbSlice';
import errorSlice, { setError } from '@store/errorSlice';
import translationSlice from '@store/translationSlice';
import userSlice from '@store/userSlice';
import { getMatchCase, isRTKApiErrorResponse } from '@utils/service';
import { matchPath } from 'react-router-dom';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    // Optionally pass options listed below
});

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejected(action)) {
        if (
            isRTKApiErrorResponse(action) &&
            (action.payload?.status === 403 || action.payload?.originalStatus === 403)
        ) {
            const pathname = new URL(window.location.pathname, 'http://stub').pathname;
            // For the event public page, ignore all 403s since it'll all be handled there
            if (matchPath('/app/events/:type/:eventId', pathname)) {
                return next(action);
            } else {
                api.dispatch(setError({ status: 403, case: getMatchCase() }));
            }
        }
    }

    return next(action);
};

export const store = configureStore({
    reducer: {
        app: appSlice,
        breadcrumb: breadcrumbSlice,
        error: errorSlice,
        user: userSlice,
        translation: translationSlice,
        printingSettings: printingSettingsSlice,
        [api.reducerPath]: api.reducer,
        [translationApi.reducerPath]: translationApi.reducer,
        [localeApi.reducerPath]: localeApi.reducer,
        [wordpressApi.reducerPath]: wordpressApi.reducer,
        [djangoCmsApi.reducerPath]: djangoCmsApi.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(
            api.middleware,
            translationApi.middleware,
            localeApi.middleware,
            wordpressApi.middleware,
            djangoCmsApi.middleware,
            rtkQueryErrorLogger
        ),
    enhancers: (getDefaultEnhancers) => {
        return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
